import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useQuery } from '@tanstack/react-query'
import { TableBody, TableCell, TableHead, TableRow } from '@mui/material'
import {
  MoovyContainer,
  MoovyContainerContent,
  MoovyContainerSubHeader,
  MoovySortAndFilter,
  MoovyTable,
  MoovyGrid,
  MoovyGridCard,
  MoovyDateTime,
  MoovyLink
} from '../../components'
import {
  MoovyPaginationProps,
  PagingAndSortingValues,
  convertPaginationValues,
  getPagingAndSortInitValues
} from '../../components/moovyPagination'
import {
  getDirection,
  getSortItems
} from '../../components/moovySortAndFilter/sortValues'
import { OfferingResponseDTO } from '../../apis/offering/dto'
import { ActiveRightSortType } from '../../apis/rights/dto'
import useGlobalUI from '../../hooks/useGlobalUI'
import { useNavigate } from 'react-router-dom'
import { appRoutes } from '../../app/permissions'
import { getJoinedValue } from '../../utils/utils'
import SearchRightHolders from '../catalogues/tabCatalogue/SearchRightHolders'
import { OfferingsAPI } from '../../apis/offering/offeringsAPI'
import {
  QUERY_OFFERING_RIGHT_HOLDERS,
  SS_OFFERING_RIGHT_HOLDERS_PAGING,
  SS_OFFERING_RIGHT_HOLDERS_SORTING
} from '../../apis/moovyQueryKeys'

export interface RightHoldersTabProps {
  offering?: OfferingResponseDTO
}

const RightHoldersTab: React.FC<RightHoldersTabProps> = ({ offering }) => {
  const navigate = useNavigate()
  const intl = useIntl()

  const [pagingAndSortValues, setPagingAndSortValues] = useGlobalUI({
    key: SS_OFFERING_RIGHT_HOLDERS_PAGING,
    initialValue: getPagingAndSortInitValues<ActiveRightSortType>('VEHICLE')
  })

  const [searchTerm, setSearchTerm] = useGlobalUI({
    key: 'moovyOfferingRightHoldersSearch',
    initialValue: { value: '' }
  })

  if (!offering) {
    return null
  }

  const queryRightHolders = useQuery({
    queryKey: [
      QUERY_OFFERING_RIGHT_HOLDERS,
      offering.id,
      pagingAndSortValues.page,
      pagingAndSortValues.sort,
      searchTerm.value
    ],
    queryFn: () => {
      const { page, limit, sort, direction } = convertPaginationValues(
        pagingAndSortValues
      ) as PagingAndSortingValues

      return OfferingsAPI.getRightHoldersByOffering(offering.id, {
        page,
        limit,
        sort: sort as ActiveRightSortType,
        direction,
        searchTerm: searchTerm.value
      })
    },
    enabled: searchTerm.value.length >= 3
  })

  const rightHolders = queryRightHolders.data?.data ?? []
  const pagination: MoovyPaginationProps | undefined = queryRightHolders.data
    ?.pageable
    ? {
        page: queryRightHolders.data.pageable.page,
        total: queryRightHolders.data.pageable.total,
        limit: queryRightHolders.data.pageable.limit,
        onNextPage: (page) =>
          setPagingAndSortValues({
            ...pagingAndSortValues,
            page: page.toString()
          })
      }
    : undefined

  const onClickRow = (row: any) => {
    if (row.individual?.id) {
      navigate(`${appRoutes.RIGHT_HOLDERS}/${row.individual?.id || ''}`, {
        state: {
          placeId: row.place?.id,
          placeName: row.place?.name,
          catalogueId: row.catalogueId,
          offeringName: offering.name
        }
      })
    } else {
      navigate(`${appRoutes.RIGHTS}/${row.id || ''}`)
    }
  }

  const items = getSortItems(intl, 'ACTIVE_RIGHTS').filter(
    (item) => offering.type !== 'WHITELIST' || item.key !== 'INDIVIDUAL'
  )

  const isWhitelist = offering.type === 'WHITELIST'

  return (
    <div>
      <MoovyContainer>
        <MoovyContainerSubHeader
          description={
            <>
              <SearchRightHolders
                initialSearch={searchTerm.value}
                onSearch={(newSearchTerm) => {
                  setSearchTerm({ value: newSearchTerm })
                  setPagingAndSortValues({ ...pagingAndSortValues, page: 1 })
                }}
                query={queryRightHolders}
              />

              <MoovySortAndFilter
                filterKey={SS_OFFERING_RIGHT_HOLDERS_SORTING}
                items={items}
                value={pagingAndSortValues.sort}
                onChange={(value) => {
                  pagingAndSortValues.sort !== value &&
                    setPagingAndSortValues({
                      ...pagingAndSortValues,
                      sort: value,
                      direction: getDirection(value as ActiveRightSortType)
                    })
                }}
              />
            </>
          }
        />
        <MoovyContainerContent>
          <MoovyGrid
            emptyTextId={
              searchTerm.value.length === 0
                ? 'page.offering.rightHoldersTab.table.search.empty'
                : 'page.offering.rightHoldersTab.table.empty'
            }
            errorTextId="page.offering.rightHoldersTab.table.error"
            query={queryRightHolders}
            pagination={pagination}
          >
            {rightHolders.map((row: any) => {
              const normalContentList = [
                <FormattedMessage id="common.text.email" />,
                <b>{row.individual?.email || ''}</b>,
                <FormattedMessage id="common.text.licensePlate" />,
                <b>{row.vehicle?.licensePlate || ''}</b>
              ]
              const commonContentList = [
                <FormattedMessage id="page.catalogues.tabCatalogue.rightHolders.card.header.place" />,
                <b>{row.place?.name}</b>,
                <FormattedMessage id="page.catalogues.tabCatalogue.rightHolders.card.header.validUntil" />,
                <b>
                  <MoovyDateTime
                    utcDateTime={row.validityEnd}
                    emptyText={intl.formatMessage({
                      id: 'page.rightHolders.rightHolder.tab.vehicles.table.item.permanent'
                    })}
                  />
                </b>
              ]
              return (
                <MoovyGridCard
                  key={row.id}
                  cardTitle={
                    isWhitelist
                      ? row.vehicle?.licensePlate || ''
                      : getJoinedValue(
                          row.individual?.lastName,
                          row.individual?.firstName
                        )
                  }
                  cardSecondaryTitle={
                    <MoovyDateTime utcDateTime={row.validityStart} />
                  }
                  cardContentList={
                    isWhitelist
                      ? commonContentList
                      : [...normalContentList, ...commonContentList]
                  }
                  onCardClicked={() => onClickRow(row)}
                />
              )
            })}
          </MoovyGrid>
          <MoovyTable
            items={rightHolders}
            emptyTextId={
              searchTerm.value.length === 0
                ? 'page.offering.rightHoldersTab.table.search.empty'
                : 'page.offering.rightHoldersTab.table.empty'
            }
            errorTextId="page.offering.rightHoldersTab.table.error"
            query={queryRightHolders}
            pagination={pagination}
          >
            <TableHead>
              <TableRow>
                {!isWhitelist && (
                  <>
                    <TableCell>
                      <FormattedMessage id="page.catalogues.tabCatalogue.rightHolders.table.header.name" />
                    </TableCell>
                    <TableCell>
                      <FormattedMessage id="common.text.email" />
                    </TableCell>
                  </>
                )}
                <TableCell>
                  <FormattedMessage id="common.text.licensePlate" />
                </TableCell>
                <TableCell>
                  <FormattedMessage id="page.catalogues.tabCatalogue.rightHolders.table.header.place" />
                </TableCell>
                <TableCell>
                  <FormattedMessage id="page.offering.rightHoldersTab.table.header.customer" />
                </TableCell>
                <TableCell>
                  <FormattedMessage id="page.catalogues.tabCatalogue.rightHolders.table.header.validFrom" />
                </TableCell>
                <TableCell>
                  <FormattedMessage id="page.catalogues.tabCatalogue.rightHolders.table.header.validUntil" />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rightHolders.map((row: any) => (
                <TableRow
                  key={row.id}
                  onClick={() => onClickRow(row)}
                  hover
                  sx={{ cursor: 'pointer' }}
                >
                  {!isWhitelist && (
                    <>
                      <TableCell>
                        <MoovyLink>
                          {getJoinedValue(
                            row.individual?.lastName,
                            row.individual?.firstName
                          )}
                        </MoovyLink>
                      </TableCell>
                      <TableCell>{row.individual?.email || ''}</TableCell>
                    </>
                  )}
                  <TableCell>
                    {row.individual ? (
                      row.vehicle?.licensePlate || ''
                    ) : (
                      <MoovyLink>{row.vehicle?.licensePlate || ''}</MoovyLink>
                    )}
                  </TableCell>
                  <TableCell>{row.place?.name}</TableCell>
                  <TableCell>{row.customerName}</TableCell>
                  <TableCell>
                    <MoovyDateTime utcDateTime={row.validityStart} />
                  </TableCell>
                  <TableCell>
                    <MoovyDateTime
                      utcDateTime={row.validityEnd}
                      emptyText={intl.formatMessage({
                        id: 'page.rightHolders.rightHolder.tab.vehicles.table.item.permanent'
                      })}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </MoovyTable>
        </MoovyContainerContent>
      </MoovyContainer>
    </div>
  )
}

export default RightHoldersTab
